import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PlayCircle, CheckCircle, ArrowRight, FileText, Users, Video, MessageSquare, HelpCircle, Image, Power, PowerOff } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import QuizModal from "@/components/QuizModal";

export default function EmployeeTrainingModules() {
  const { toast } = useToast();
  const [selectedDay, setSelectedDay] = useState(1);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [isCodeOfConductOpen, setIsCodeOfConductOpen] = useState(false);
  const [isReferBuddyOpen, setIsReferBuddyOpen] = useState(false);
  const [isHelpdeskOpen, setIsHelpdeskOpen] = useState(false);
  const [isMCQOpen, setIsMCQOpen] = useState(false);
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState<any>(null);
  const [currentModule, setCurrentModule] = useState<any>(null);
  const [mcqAnswers, setMcqAnswers] = useState<string[]>([]);

  // Function to get appropriate icon for each module type
  const getModuleIcon = (module: any) => {
    if (module.isCompleted) {
      return <CheckCircle className="text-white h-8 w-8" />;
    }
    
    // Return specific icons based on module type
    switch (module.mtype) {
      case "video":
        return <Video className="text-white h-8 w-8" />;
      case "image":
        return <Image className="text-white h-8 w-8" />;
      case "form":
        return <FileText className="text-white h-8 w-8" />;
      case "helpdesk":
        return <FileText className="text-white h-8 w-8" />;
      case "external":
        return <MessageSquare className="text-white h-8 w-8" />;
      default:
        return <PlayCircle className="text-white h-8 w-8" />;
    }
  };

  const { data: modules, isLoading } = useQuery({
    queryKey: ['/training-modules'],
  });

  const { data: progress } = useQuery({
    queryKey: ['/progress'],
  }) as { data: any };

  const { data: user } = useQuery({
    queryKey: ['/auth/me'],
  }) as { data: any };

  // Check if user is admin
  const isAdmin = user?.user?.role === 'admin' || user?.role === 'admin';

  const { data: quizzesData } = useQuery({
    queryKey: ['/quizzes'],
  }) as { data: any };

  const queryClient = useQueryClient();

  // Mutation for updating progress
  const updateProgressMutation = useMutation({
    mutationFn: async (progressData: { moduleId: string; isCompleted: boolean; timeSpent?: number }) => {
      console.log("Updating progress:", progressData);
      const response = await apiRequest("POST", "/progress", progressData);
      const result = await response.json();
      console.log("Progress update response:", result);
      return result;
    },
    onSuccess: (data) => {
      console.log("Progress update successful:", data);
      // Invalidate and refetch all progress-related data
      queryClient.invalidateQueries({ queryKey: ['/progress'] });
      queryClient.invalidateQueries({ queryKey: ['/progress/overall'] });
      queryClient.invalidateQueries({ queryKey: ['/training-modules'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: "Failed to update progress",
        variant: "destructive",
      });
    },
  });

  // Mutation for creating quiz attempts
  const createQuizAttemptMutation = useMutation({
    mutationFn: async (attemptData: { quizId: string; score: number; isPassed: boolean; answers: any }) => {
      const response = await apiRequest("POST", "/quiz-attempts", attemptData);
      return response.json();
    },
    onSuccess: (data) => {
      console.log("Quiz attempt created successfully:", data);
      // Invalidate and refetch quiz attempts data immediately
      queryClient.invalidateQueries({ queryKey: ['/quiz-attempts'] });
      queryClient.refetchQueries({ queryKey: ['/quiz-attempts'] });
      
      // Also invalidate progress to ensure everything is updated
      queryClient.invalidateQueries({ queryKey: ['/progress'] });
      queryClient.invalidateQueries({ queryKey: ['/progress/overall'] });
      
      toast({
        title: data.attempt.isPassed ? "Quiz Passed! 🎉" : "Quiz Completed",
        description: data.attempt.isPassed 
          ? `Congratulations! You scored ${data.attempt.score}%` 
          : `You scored ${data.attempt.score}%. Keep practicing!`,
        variant: data.attempt.isPassed ? "default" : "destructive",
      });
    },
    onError: (error: Error) => {
      console.error("Quiz attempt creation error:", error);
      toast({
        title: "Error",
        description: "Failed to submit quiz attempt",
        variant: "destructive",
      });
    },
  });

  // Mutation for updating module status (admin only)
  const toggleModuleStatusMutation = useMutation({
    mutationFn: async ({ moduleId, isActive }: { moduleId: string; isActive: boolean }) => {
      const response = await apiRequest("PUT", `/training-modules/${moduleId}`, { isActive });
      return response.json();
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch training modules
      queryClient.invalidateQueries({ queryKey: ['/training-modules'] });
      
      toast({
        title: "Success",
        description: `Module ${variables.isActive ? 'activated' : 'deactivated'} successfully`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: "Failed to update module status",
        variant: "destructive",
      });
    },
  });

  // Handler for toggle module status (admin only)
  const handleToggleModuleStatus = (module: any, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const newStatus = module.isActive === false;
    const action = newStatus ? 'activate' : 'deactivate';
    
    const confirmed = window.confirm(
      `Are you sure you want to ${action} the training module "${module.title}"?${
        !newStatus ? '\n\nThis will hide the module from all employees immediately.' : ''
      }`
    );
    
    if (confirmed) {
      toggleModuleStatusMutation.mutate({
        moduleId: module.id,
        isActive: newStatus
      });
    }
  };

  const handleVideoComplete = () => {
    console.log("Video completed for module:", currentModule);
    console.log("Current module details:", {
      id: currentModule?.id,
      title: currentModule?.title,
      startTime: currentModule?.startTime,
      currentTime: Date.now()
    });
    console.log("Quizzes data:", quizzesData);
    console.log("Current module ID:", currentModule?.id, "Type:", typeof currentModule?.id);
    
    if (currentModule) {
      // First, check if this module has a linked quiz (works for ALL modules)
      // Handle both string and number comparisons
      const moduleId = typeof currentModule.id === 'string' ? parseInt(currentModule.id) : currentModule.id;
      // Handle both array and object response formats
      const quizzesArray = Array.isArray(quizzesData) ? quizzesData : (quizzesData?.quizzes || []);
      console.log("Quizzes array:", quizzesArray);
      
      const quiz = quizzesArray.find((q: any) => {
        const quizModuleId = typeof q.moduleId === 'string' ? parseInt(q.moduleId) : q.moduleId;
        console.log(`Comparing moduleId: ${moduleId} (${typeof moduleId}) with quiz.moduleId: ${quizModuleId} (${typeof quizModuleId})`);
        return quizModuleId === moduleId;
      });
      
      console.log("Found quiz:", quiz);
      
      if (quiz) {
        console.log(`Module "${currentModule.title}" has a linked quiz, showing quiz modal...`);
        setIsVideoOpen(false);
        setCurrentQuiz(quiz);
        setIsQuizOpen(true);
        return;
      }

      // For Code of Conduct video, show MCQ after completion (fallback if no linked quiz)
      if (currentModule.title === "Code of Conduct") {
        console.log("Opening MCQ modal, resetting mcqAnswers");
        setIsVideoOpen(false);
        setIsMCQOpen(true);
        setMcqAnswers([]); // Reset answers when opening MCQ
        return;
      }

      // For Refer a Buddy video, show quiz after completion (fallback if no linked quiz)
      if (currentModule.title === "Refer a Buddy") {
        console.log("Refer A Buddy video completed, opening quiz...");
        setIsVideoOpen(false);
        setIsReferBuddyOpen(true);
        return;
      }

      // For other videos without quizzes, mark as completed
      const timeSpent = Math.floor((Date.now() - (currentModule.startTime || Date.now())) / 1000);
      console.log(`Marking ${currentModule.title} as completed, timeSpent: ${timeSpent}s`);
      
      updateProgressMutation.mutate({
        moduleId: currentModule.id,
        isCompleted: true,
        timeSpent: Math.max(timeSpent, 300),
      });

      toast({
        title: "Module Completed!",
        description: `${currentModule.title} has been marked as completed.`,
      });
    } else {
      console.log("No current module found");
    }
  };

  const handleMCQOptionToggle = (option: string) => {
    console.log("MCQ option toggled:", option);
    setMcqAnswers(prev => {
      const newAnswers = prev.includes(option) 
        ? prev.filter(item => item !== option)
        : [...prev, option];
      console.log("Updated MCQ answers:", newAnswers);
      return newAnswers;
    });
  };

  const handleMCQSubmit = () => {
    console.log("MCQ Submit clicked!");
    console.log("Current MCQ answers:", mcqAnswers);
    
    // Get the quiz data from the database
    const quizzesArray = Array.isArray(quizzesData) ? quizzesData : (quizzesData?.quizzes || []);
    const quiz = quizzesArray.find((q: any) => {
      const quizModuleId = typeof q.moduleId === 'string' ? parseInt(q.moduleId) : q.moduleId;
      const moduleId = typeof currentModule?.id === 'string' ? parseInt(currentModule.id) : currentModule?.id;
      return quizModuleId === moduleId;
    });
    
    if (!quiz) {
      console.error("Quiz not found for module:", currentModule?.id);
      toast({
        title: "Error",
        description: "Quiz data not found. Please try again.",
        variant: "destructive",
      });
      return;
    }
    
    // Get the question and correct answers from the database
    const question = quiz.questions[0]; // First question
    const correctAnswerIndices = question.correctAnswer; // Array of correct indices
    const correctAnswers = correctAnswerIndices.map((index: number) => question.options[index]);
    
    // Calculate score based on how many correct answers were selected
    const correctlySelected = correctAnswers.filter((answer: string) => mcqAnswers.includes(answer)).length;
    const incorrectlySelected = mcqAnswers.filter((answer: string) => !correctAnswers.includes(answer)).length;
    
    // Calculate score percentage: (correct answers selected - incorrect answers selected) / total correct answers
    // Ensure score doesn't go below 0
    const rawScore = Math.max(0, (correctlySelected - incorrectlySelected) / correctAnswers.length);
    const score = Math.round(rawScore * 100);
    const isPassed = score >= quiz.passingScore;
    
    console.log("Correctly selected:", correctlySelected);
    console.log("Incorrectly selected:", incorrectlySelected);
    console.log("Score:", score);
    console.log("Correct answers:", correctAnswers);
    console.log("Selected answers:", mcqAnswers);
    console.log("Quiz data:", quiz);
    
    // Save quiz attempt to database using the actual quiz ID
    createQuizAttemptMutation.mutate({
      quizId: quiz.id,
      score: score,
      isPassed: isPassed,
      answers: mcqAnswers
    });
    
    if (isPassed) {
      console.log("MCQ passed, showing success toast");
      toast({
        title: "Knowledge Check Passed!",
        description: `Great job! You've successfully completed the Code of Conduct knowledge check with ${score}% score.`,
      });
      
      // Mark Code of Conduct as completed in database
      if (currentModule) {
        console.log("Marking module as completed:", currentModule.id);
        updateProgressMutation.mutate({
          moduleId: currentModule.id,
          isCompleted: true,
          timeSpent: 600, // 10 minutes for quiz completion
        });
      }
    } else {
      console.log("MCQ failed, showing error toast");
      toast({
        title: "Knowledge Check Failed",
        description: `Your score: ${score}%. Please review the Code of Conduct and try again. All options apply.`,
        variant: "destructive",
      });
      return; // Don't close modal if incorrect
    }
    
    console.log("Closing MCQ modal");
    // Close MCQ modal and reset
    setIsMCQOpen(false);
    setMcqAnswers([]);
    setCurrentModule(null);
  };

  const handleModuleClick = (module: any) => {
    console.log("Module clicked:", module);
    console.log("Module type:", module.mtype);
    
    switch (module.mtype) {
      case "video":
        console.log("Opening video modal for:", module.title);
        setIsVideoOpen(true);
        const moduleWithStartTime = { ...module, startTime: Date.now() };
        console.log("Setting current module with startTime:", moduleWithStartTime);
        setCurrentModule(moduleWithStartTime);
        break;
      case "image":
        console.log("Opening image modal for:", module.title);
        setIsImageOpen(true);
        setCurrentModule(module);
        break;
      case "form":
        console.log("Opening form for:", module.title);
        if (module.externalUrl) {
          window.open(module.externalUrl, '_blank');
        }
        break;
      case "helpdesk":
        console.log("Opening helpdesk for:", module.title);
        setIsHelpdeskOpen(true);
        break;
      default:
        // Handle other module types
        console.log("Module type not handled:", module.mtype);
    }
  };

  // Filter modules by selected day, only show active modules, and sort by sequence
  const filteredModules = (modules as any)?.modules?.filter((module: any) => 
    module.day === selectedDay && module.isActive !== false
  ) || [];

  // Add completion status to modules and sort by sequence
  const modulesWithProgress = filteredModules
    .map((module: any) => {
      const progressItem = progress?.progress?.find((p: any) => p.moduleId === module.id);
      return {
        ...module,
        isCompleted: progressItem?.isCompleted || false,
      };
    })
    .sort((a: any, b: any) => a.sequence - b.sequence);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-servify-purple mx-auto mb-4"></div>
            <p className="text-gray-600">Loading training modules...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header Section */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="text-page-title">Knowledge Hub</h1>
          <p className="text-gray-600 mt-1" data-testid="text-page-description">
            Complete all items to finish your induction program
          </p>
        </div>

        {/* Day Navigation Tabs with Completion Status */}
        <div className="flex items-center justify-between">
          <div className="flex space-x-2">
            {[1, 2, 3].map((day) => (
              <Button
                key={day}
                variant={selectedDay === day ? "default" : "outline"}
                className={`${
                  selectedDay === day 
                    ? 'bg-servify-purple text-white hover:bg-servify-purple-dark' 
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                } rounded-lg px-6 py-2`}
                onClick={() => setSelectedDay(day)}
                data-testid={`button-day-${day}`}
              >
                Day {day}
              </Button>
            ))}
          </div>
        </div>

        {/* Modules List */}
        <div className="space-y-4" data-testid="modules-list">
          {modulesWithProgress.map((module: any) => (
            <Card 
              key={module.id} 
              className={`transition-all duration-200 hover:shadow-md cursor-pointer bg-white border border-gray-200 ${
                // Admin visual indicators for inactive modules
                isAdmin && module.isActive === false 
                  ? 'border-orange-200 bg-orange-50/30' 
                  : ''
              } rounded-lg`}
              onClick={() => handleModuleClick(module)}
              data-testid={`module-card-${module.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    {/* Module Icon */}
                    <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${
                      module.isCompleted ? 'bg-green-500' : 'bg-servify-purple'
                    }`}>
                      {getModuleIcon(module)}
                    </div>
                    
                    {/* Module Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h2 className="text-xl font-semibold text-gray-900" data-testid={`text-module-title-${module.id}`}>
                          {module.title}
                        </h2>
                        {module.isCompleted && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                            Completed
                          </span>
                        )}
                        {/* Admin-only status indicator */}
                        {isAdmin && (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            module.isActive !== false 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {module.isActive !== false ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </div>
                      
                      <p className="text-gray-600 mb-2" data-testid={`text-module-description-${module.id}`}>
                        {module.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Right side - Admin controls or Arrow */}
                  <div className="flex items-center space-x-2">
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => handleToggleModuleStatus(module, e)}
                        className={`px-3 py-2 font-medium ${
                          module.isActive !== false 
                            ? 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100 hover:border-orange-300'
                            : 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100 hover:border-green-300'
                        }`}
                        disabled={toggleModuleStatusMutation.isPending}
                        data-testid={`button-toggle-module-${module.id}`}
                      >
                        {module.isActive !== false ? (
                          <>
                            <PowerOff className="h-4 w-4 mr-1" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <Power className="h-4 w-4 mr-1" />
                            Activate
                          </>
                        )}
                      </Button>
                    )}
                    <ArrowRight className="text-gray-400 h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        
          {modulesWithProgress.length === 0 && (
            <Card className="p-8 text-center" data-testid="no-modules-message">
              <div className="max-w-md mx-auto">
                <PlayCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Knowledge Hub Modules for Day {selectedDay}</h3>
                <p className="text-gray-600">
                  Knowledge hub modules for this day will appear here once they are created by administrators.
                </p>
              </div>
            </Card>
          )}
        </div>

        {/* Video Modal */}
        {isVideoOpen && currentModule && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
              {/* Modal Header */}
              <div className="bg-servify-purple text-white p-6 relative">
                <h2 className="text-2xl font-bold text-center">{currentModule.title}</h2>
                <p className="text-servify-purple/20 text-center mt-2">{currentModule.description}</p>
                
                {/* Close Button */}
                <button
                  onClick={() => setIsVideoOpen(false)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Video Content */}
              <div className="p-6 flex flex-col items-center">
                <video
                  controls
                  className="w-full h-auto max-w-3xl rounded-xl shadow-lg no-seek-video"
                  controlsList="nodownload noplaybackrate"
                  disablePictureInPicture
                  autoPlay
                  playsInline
                  tabIndex={0}
                  onEnded={handleVideoComplete}
                  onLoadedMetadata={(e) => {
                    const video = e.currentTarget;
                    video.dataset.lastTime = "0";
                    video.focus();
                  }}
                  onSeeking={(e) => {
                    e.preventDefault();
                    const video = e.currentTarget;
                    const lastTime = parseFloat(video.dataset.lastTime || "0");
                    video.currentTime = lastTime;
                  }}
                  onSeeked={(e) => {
                    e.preventDefault();
                    const video = e.currentTarget;
                    const lastTime = parseFloat(video.dataset.lastTime || "0");
                    video.currentTime = lastTime;
                  }}
                  onTimeUpdateCapture={(e) => {
                    const video = e.currentTarget;
                    video.dataset.lastTime = video.currentTime.toString();
                  }}
                  onRateChange={(e) => {
                    e.preventDefault();
                    const video = e.currentTarget;
                    if (video.playbackRate !== 1) {
                      video.playbackRate = 1;
                    }
                  }}
                  onKeyDown={(e) => {
                    if (['ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onLoadedData={(e) => {
                    const video = e.currentTarget;
                    video.play().catch(error => {
                      console.log("Autoplay failed:", error);
                    });
                  }}
                  data-testid="module-video"
                >
                  <source src={currentModule.externalUrl || currentModule.mediaFile || ""} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        )}

        {/* Image Modal */}
        {isImageOpen && currentModule && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
              {/* Modal Header */}
              <div className="bg-servify-purple text-white p-6 relative">
                <h2 className="text-2xl font-bold text-center">{currentModule.title}</h2>
                <p className="text-servify-purple/20 text-center mt-2">{currentModule.description}</p>
                
                {/* Close Button */}
                <button
                  onClick={() => setIsImageOpen(false)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Image Content */}
              <div className="p-6 flex flex-col items-center">
                <img
                  src={currentModule.externalUrl || currentModule.mediaFile || ""}
                  alt={currentModule.title}
                  className="w-full h-auto max-w-3xl rounded-xl shadow-lg"
                  data-testid="module-image"
                />
              </div>
            </div>
          </div>
        )}

        {/* MCQ Modal */}
        {isMCQOpen && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden relative">
              {/* Modal Header */}
              <div className="bg-servify-purple text-white p-6 relative">
                <h2 className="text-2xl font-bold text-center">Code of Conduct Knowledge Check</h2>
                <p className="text-servify-purple/20 text-center mt-2">Test your understanding of the Code of Conduct</p>
                
                {/* Close Button */}
                <button
                  onClick={() => {
                    setIsMCQOpen(false);
                    setMcqAnswers([]);
                    setCurrentModule(null);
                  }}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* MCQ Content */}
              <div className="p-8">
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Whom does the Code of Conduct apply to? Click all options that apply.
                  </h3>                
                  <div className="space-y-3">
                    {['Employees', 'Consultants', 'Partners', 'Customers'].map((option) => (
                      <label key={option} className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg border-2 hover:bg-gray-50 transition-colors">
                        <input
                          type="checkbox"
                          checked={mcqAnswers.includes(option)}
                          onChange={() => handleMCQOptionToggle(option)}
                          className="w-5 h-5 text-servify-purple border-gray-300 rounded focus:ring-servify-purple/20"
                        />
                        <span className="text-gray-700 font-medium">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setIsMCQOpen(false);
                      setMcqAnswers([]);
                      setCurrentModule(null);
                    }}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleMCQSubmit}
                    className="px-6 py-2 bg-servify-purple text-white rounded-lg hover:bg-servify-purple-dark transition-colors"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Refer A Buddy Quiz Modal */}
        {isReferBuddyOpen && (() => {
          const quizzesArray = Array.isArray(quizzesData) ? quizzesData : (quizzesData?.quizzes || []);
          const referBuddyQuiz = quizzesArray.find((q: any) =>
            q.title === "Go Refer A Buddy (GRAB Policy) Quiz"
          );
          const question = referBuddyQuiz?.questions?.[0];
          
          const modalContent = (
            <div className="modal-backdrop fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0, padding: '1rem' }}>
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden relative">
                {/* Modal Header */}
                <div className="bg-servify-purple text-white p-6 relative">
                  <h2 className="text-2xl font-bold text-center">Refer A Buddy Quiz</h2>
                  <p className="text-servify-purple/20 text-center mt-2">Test your understanding of the Refer A Buddy policy</p>
                  
                  {/* Close Button */}
                  <button
                    onClick={() => {
                      setIsReferBuddyOpen(false);
                      setMcqAnswers([]);
                      setCurrentModule(null);
                    }}
                    className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                  >
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Quiz Content */}
                <div className="p-8">
                  <div className="mb-6">
                    {question ? (
                      <>
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">
                          {question.question}
                        </h3>                
                        <div className="space-y-3">
                          {question.options.map((option: string, index: number) => (
                            <label key={option} className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg border-2 hover:bg-gray-50 transition-colors">
                              <input
                                type="checkbox"
                                checked={mcqAnswers.includes(option)}
                                onChange={() => handleMCQOptionToggle(option)}
                                className="w-5 h-5 text-servify-purple border-gray-300 rounded focus:ring-servify-purple/20"
                              />
                              <span className="text-gray-900 font-medium">{option}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    ) : (
                      <p className="text-red-500">Quiz data not found</p>
                    )}
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setIsReferBuddyOpen(false);
                        setMcqAnswers([]);
                        setCurrentModule(null);
                      }}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        // Handle Refer A Buddy quiz submission
                        const quizzesArray = Array.isArray(quizzesData) ? quizzesData : (quizzesData?.quizzes || []);
                        const referBuddyQuiz = quizzesArray.find((q: any) =>
                          q.title === "Go Refer A Buddy (GRAB Policy) Quiz"
                        );
                        
                        if (referBuddyQuiz) {
                          const question = referBuddyQuiz.questions[0];
                          const correctAnswers = question.correctAnswer.map((index: number) => question.options[index]);
                          const isCorrect = correctAnswers.every((answer: string) => mcqAnswers.includes(answer)) &&
                                           mcqAnswers.length === correctAnswers.length;
                          
                          if (isCorrect) {
                            toast({
                              title: "Quiz Passed! 🎉",
                              description: "Congratulations! You've successfully completed the Refer A Buddy quiz.",
                            });
                            
                            // Mark Refer A Buddy as completed
                            if (currentModule) {
                              updateProgressMutation.mutate({
                                moduleId: currentModule.id,
                                isCompleted: true,
                                timeSpent: 600,
                              });
                            }
                            
                            setIsReferBuddyOpen(false);
                            setMcqAnswers([]);
                            setCurrentModule(null);
                          } else {
                            toast({
                              title: "Quiz Failed",
                              description: "Please review the Refer A Buddy policy and try again.",
                              variant: "destructive",
                            });
                          }
                        }
                      }}
                      className="px-6 py-2 bg-servify-purple text-white rounded-lg hover:bg-servify-purple-dark transition-colors"
                    >
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
          
          return createPortal(modalContent, document.body);
        })()}

        {/* Helpdesk Modal */}
        {isHelpdeskOpen && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
              {/* Modal Header */}
              <div className="bg-servify-purple text-white p-6 relative">
                <h2 className="text-2xl font-bold text-center">Helpdesk Guide</h2>
                <p className="text-servify-purple/20 text-center mt-2">Here's a quick and easy guide to our Servify Apps Helpdesk — so you know exactly whom to reach out to.</p>
                
                {/* Close Button */}
                <button
                  onClick={() => setIsHelpdeskOpen(false)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L6 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Helpdesk Content */}
              <div className="p-6 max-h-[70vh] overflow-y-auto">
                <div className="space-y-8">
                  {/* HR Support Section */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-xl font-bold text-servify-purple mb-4 flex items-center">
                      <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      HR Support
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                        <span className="font-semibold text-gray-900">Servify LaunchPad</span>
                        <a href="mailto:poonam.m@servify.com" className="text-servify-purple hover:underline font-medium">poonam.m@servify.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                        <span className="font-semibold text-gray-900">Oracle HCM</span>
                        <a href="mailto:radhika.j@servify.com" className="text-servify-purple hover:underline font-medium">radhika.j@servify.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                        <span className="font-semibold text-gray-900">Zoho Expense</span>
                        <a href="mailto:shrutika.c@servify.com" className="text-servify-purple hover:underline font-medium">shrutika.c@servify.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                        <span className="font-semibold text-gray-900">ADP</span>
                        <a href="mailto:apeksha.m@servify.com" className="text-servify-purple hover:underline font-medium">apeksha.m@servify.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                        <span className="font-semibold text-gray-900">TAS</span>
                        <a href="mailto:abhishek.j@servify.com" className="text-servify-purple hover:underline font-medium">abhishek.j@servify.com</a>
                      </div>
                    </div>
                  </div>

                  {/* IT Support Section */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-xl font-bold text-servify-purple mb-4 flex items-center">
                      <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      IT Support
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                        <span className="font-semibold text-gray-900">General IT Issues</span>
                        <a href="mailto:it-support@servify.com" className="text-servify-purple hover:underline font-medium">it-support@servify.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                        <span className="font-semibold text-gray-900">Slack Support</span>
                        <a href="https://servify.slack.com/archives/C01EPF1MJTU" target="_blank" rel="noopener noreferrer" className="text-servify-purple hover:underline font-medium">servify.slack.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                        <span className="font-semibold text-gray-900">IT Helpdesk</span>
                        <a href="mailto:it_helpdesk@servify.com" className="text-servify-purple hover:underline font-medium">it_helpdesk@servify.com</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quiz Modal */}
        <QuizModal
          isOpen={isQuizOpen}
          onClose={() => {
            setIsQuizOpen(false);
            setCurrentQuiz(null);
          }}
          quiz={currentQuiz}
          isAdmin={false}
          onQuizComplete={() => {
            setIsQuizOpen(false);
            setCurrentQuiz(null);
            setCurrentModule(null);
          }}
        />
      </div>
    </div>
  );
} 